import React, { useState } from 'react';
import { Printer, FilePdf } from '@phosphor-icons/react';
import AlertDialog from './AlertDialog';

// Selection Guide Modal (printable help) — pure presentational component.
export const SelectionGuideModal = ({ open, onClose }) => {
  const [notice, setNotice] = useState(null);
  // Build the printable popup programmatically (no document.write, no string-based
  // HTML injection). The cloned content has already been React-rendered so it's safe,
  // but we still strip class/style attributes and use direct DOM APIs to avoid any
  // pattern that static analysers would flag as XSS-prone.
  const openPrintablePopup = (kind = 'print') => {
    const source = document.getElementById('selection-guide-printable');
    if (!source) return;
    const inner = source.querySelector('.space-y-6');
    if (!inner) return;

    // Clone + sanitise: drop Tailwind utility classes so the popup uses ONLY its own CSS.
    const cleanedClone = inner.cloneNode(true);
    cleanedClone.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));
    cleanedClone.querySelectorAll('[style]').forEach((el) => el.removeAttribute('style'));

    const popup = window.open('', 'pro75-guide-print', 'width=900,height=1000');
    if (!popup) {
      setNotice('Please allow popups for this site to print or save the Help guide as PDF.');
      return;
    }

    const doc = popup.document;
    // Reset the popup document and rebuild from scratch.
    doc.documentElement.replaceChildren();

    // <head>
    const head = doc.createElement('head');
    const meta = doc.createElement('meta'); meta.setAttribute('charset', 'utf-8'); head.appendChild(meta);
    const title = doc.createElement('title'); title.textContent = "Raisey's Smart Saver — Help"; head.appendChild(title);
    const style = doc.createElement('style');
    style.textContent = `
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        color: #111; background: #fff;
        padding: 1.5cm; line-height: 1.5; font-size: 11pt;
      }
      h1 { font-size: 22pt; font-weight: 700; margin: 0 0 4pt; color: #111; }
      h2 { font-size: 14pt; font-weight: 600; margin: 0 0 14pt; color: #333; }
      h3 { font-size: 13pt; font-weight: 700; margin: 18pt 0 6pt; color: #111; }
      hr { border: 0; border-top: 1px solid #000; margin: 8pt 0 16pt; }
      p { margin: 0 0 6pt; }
      ul, ol { margin: 0 0 10pt 18pt; padding: 0; }
      li { margin-bottom: 3pt; }
      strong { font-weight: 700; }
      em { color: #555; font-style: italic; }
      table { width: 100%; border-collapse: collapse; margin: 8pt 0; font-size: 10pt; }
      th, td { border: 1px solid #000; padding: 5pt 7pt; text-align: left; vertical-align: top; }
      thead th { background: #e5e5e5; }
      section { page-break-inside: avoid; margin-bottom: 16pt; }
      .header { margin-bottom: 14pt; }
      .muted { color: #555; font-size: 9pt; }
      .toolbar {
        position: fixed; top: 12px; right: 12px;
        background: #111; color: #fff; padding: 8px 12px;
        border-radius: 8px; font-size: 11px; opacity: 0.9;
        display: flex; gap: 8px; align-items: center;
      }
      .toolbar button {
        background: #fff; color: #000; border: 0; padding: 4px 10px;
        border-radius: 4px; cursor: pointer; font-size: 11px; font-weight: 600;
      }
      @media print {
        .toolbar { display: none !important; }
        body { padding: 1cm; }
      }
    `;
    head.appendChild(style);

    // <body>
    const body = doc.createElement('body');

    // Toolbar — built via DOM APIs so event handlers are attached, not inline onclick=.
    const toolbar = doc.createElement('div');
    toolbar.className = 'toolbar';
    toolbar.id = 'toolbar';
    const hint = doc.createElement('span');
    if (kind === 'pdf') {
      hint.append('Choose ');
      const b = doc.createElement('strong'); b.textContent = 'Save as PDF'; hint.appendChild(b);
      hint.append(' in the print dialog');
    } else {
      hint.textContent = 'Ready to print';
    }
    toolbar.appendChild(hint);
    const printBtn = doc.createElement('button'); printBtn.textContent = 'Print again';
    printBtn.addEventListener('click', () => popup.print());
    toolbar.appendChild(printBtn);
    const closeBtn = doc.createElement('button'); closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => popup.close());
    toolbar.appendChild(closeBtn);
    body.appendChild(toolbar);

    // Header
    const header = doc.createElement('div'); header.className = 'header';
    const h1 = doc.createElement('h1'); h1.textContent = "Raisey's Smart Saver"; header.appendChild(h1);
    const h2 = doc.createElement('h2'); h2.textContent = 'Smart Saver Help — Quick Reference'; header.appendChild(h2);
    header.appendChild(doc.createElement('hr'));
    if (kind === 'pdf') {
      const note = doc.createElement('p'); note.className = 'muted';
      note.append('When the print dialog opens, choose ');
      const s = doc.createElement('strong'); s.textContent = '"Save as PDF"'; note.appendChild(s);
      note.append(' as the destination to download a PDF.');
      header.appendChild(note);
    }
    body.appendChild(header);

    // Content — adopt the (sanitised) cloned node into the popup's document.
    body.appendChild(doc.importNode(cleanedClone, true));

    doc.documentElement.appendChild(head);
    doc.documentElement.appendChild(body);

    // Fire the print dialog once layout is ready. No auto-close (race with print dialog).
    const trigger = () => {
      try { popup.focus(); } catch (_) { /* cross-origin focus may throw — safe to ignore */ }
      popup.print();
    };
    if (doc.readyState === 'complete') setTimeout(trigger, 150);
    else popup.addEventListener('load', () => setTimeout(trigger, 150));
  };

  if (!open) return null;

  const th = 'text-left p-2 border-b border-border-color print:border-black';
  const td = 'p-2 border-b border-border-color print:border-black';

  return (
    <div
      id="selection-guide-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="guide-modal-overlay"
    >
      <div
        className="bg-surface border border-border-color rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto print:bg-white print:text-black print:max-h-none print:max-w-none print:border-0 print:rounded-none print:shadow-none"
        onClick={(e) => e.stopPropagation()}
        id="selection-guide-printable"
      >
        <div className="p-6 print:p-0">
          <div className="flex items-start justify-between mb-4 print:hidden">
            <h2 className="text-2xl font-bold font-heading text-gold">Smart Saver Help</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openPrintablePopup('print')}
                data-testid="print-guide-button"
                className="flex items-center gap-1 px-3 py-1.5 border border-gold text-gold text-sm rounded-lg hover:bg-gold hover:text-black"
              >
                <Printer size={16} weight="bold" /> Print
              </button>
              <button
                onClick={() => openPrintablePopup('pdf')}
                data-testid="save-pdf-guide-button"
                className="flex items-center gap-1 px-3 py-1.5 border border-gold text-gold text-sm rounded-lg hover:bg-gold hover:text-black"
                title='Opens the print dialog — choose "Save as PDF" as the destination.'
              >
                <FilePdf size={16} weight="bold" /> Save as PDF
              </button>
              <button
                onClick={onClose}
                data-testid="close-guide-modal"
                className="text-text-secondary hover:text-text-primary text-2xl leading-none px-2"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>

          <div className="print:block hidden mb-4">
            <h1 className="text-3xl font-bold mb-2">Raisey&apos;s Smart Saver</h1>
            <h2 className="text-xl font-semibold">Smart Saver Help — Quick Reference</h2>
            <hr className="my-3 border-black" />
          </div>

          <div className="space-y-6 text-sm text-text-primary print:text-black print:space-y-4">
            <p className="text-text-secondary print:text-gray-700">
              Pick a protein, tick <strong>Box</strong> or <strong>Pallet Layer</strong>,
              add supporting items until the green tick appears, then save.
            </p>

            <section>
              <h3 className="text-lg font-bold text-gold mb-2 print:text-black">The two Saver types</h3>
              <table className="w-full border border-border-color text-xs print:border-black">
                <thead>
                  <tr className="bg-black/40 print:bg-gray-200">
                    <th className={th}>Type</th>
                    <th className={th}>Protein</th>
                    <th className={th}>Supporting items</th>
                    <th className={th}>Range rules</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={td}><strong>Box</strong></td>
                    <td className={td}>1+ boxes, partials on top<br />(8–39 × 1 kg or 6–29 × 2 kg)</td>
                    <td className={td}>1 per 1 kg tub<br />2 per 2 kg tub</td>
                    <td className={td}>Any mix — duplicates fine.<br />2 kg: max 1 of each flavour per tub.</td>
                  </tr>
                  <tr>
                    <td className="p-2"><strong>Pallet Layer</strong></td>
                    <td className="p-2">5+ boxes of one protein</td>
                    <td className="p-2">Same, per tub</td>
                    <td className="p-2">None — any ranges, any mix.</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section>
              <h3 className="text-lg font-bold text-gold mb-2 print:text-black">Good to know</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Supporting items</strong> = anything from the Performance, Goal, or Essentials sections.</li>
                <li><strong>Pure Series Aminos</strong>: each flavour counts as its own product.</li>
                <li>You can save several Savers — they all collect in your cart for one order.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-bold text-gold mb-2 print:text-black">Step-by-step</h3>
              <ol className="list-decimal list-inside space-y-1">
                <li>On a protein card, tick <strong>Box</strong> or <strong>Pallet Layer</strong>.</li>
                <li>Set quantity and flavours with <strong>+ / −</strong>.</li>
                <li>Add supporting items until the tick turns green.</li>
                <li>Name it (optional) → <strong>Save to Saver List</strong>.</li>
                <li>Submit from <strong>Browse &amp; Order</strong>.</li>
              </ol>
            </section>

            <section>
              <h3 className="text-lg font-bold text-gold mb-2 print:text-black">Traffic lights</h3>
              <ul className="list-disc list-inside space-y-1">
                <li><span className="text-success print:text-green-700"><strong>Green</strong></span> — valid. Save it.</li>
                <li><span className="text-warning print:text-yellow-700"><strong>Amber</strong></span> — something&apos;s missing; the box says what.</li>
                <li><span className="text-danger print:text-red-700"><strong>Red</strong></span> — over a limit; the box says what to remove.</li>
              </ul>
            </section>

            <p className="text-text-secondary print:text-gray-700 text-xs">
              Stuck? Email <strong>aaron@raiseys.co.nz</strong> with a screenshot.
            </p>
          </div>
        </div>
      </div>
      <AlertDialog
        open={!!notice}
        title="Popups blocked"
        message={notice}
        variant="warning"
        onClose={() => setNotice(null)}
        testIdPrefix="guide-popup-blocked-alert"
      />
    </div>
  );
};

export default SelectionGuideModal;
